import React, { useState } from 'react';
import usePDA1120_R01Store from './usePDA1120_R01Store';
import { useBarcodeScan } from 'src/hooks/useBarcodeScan';
import { useToast } from 'src/hooks/useToast';

interface PDA1120_R01InfoProps {
  onScanLot: (lotNo?: string) => Promise<boolean>;
  checkedCount?: number;
  onMount: () => void;
  onComplete: () => Promise<void>;
  onRemoveCheckItem: (lotNo: string) => void;
  onCancelAll: () => void;
}

const copyToClipboard = (text: string): boolean => {
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(text);
    return true;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    document.execCommand('copy');
    return true;
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
};

const PDA1120_R01Info: React.FC<PDA1120_R01InfoProps> = ({
  onScanLot,
  onMount,
  onComplete,
  onRemoveCheckItem,
  onCancelAll,
}) => {
  const {
    selectedOrder,
    barcode,
    setBarcode,
    scannedLot,
    checkList,
    machineBarcode,
    machineFeederId,
    setMachineBarcode,
    setMachineFeederId,
    resetMachineScan,
  } = usePDA1120_R01Store();

  const { warning } = useToast();

  // 자재 스캔 키보드 제어
  const [materialKeyboardEnabled, setMaterialKeyboardEnabled] = useState(false);
  const [materialLastTapTime, setMaterialLastTapTime] = useState(0);

  // 기계 스캔 키보드 제어
  const [machineKeyboardEnabled, setMachineKeyboardEnabled] = useState(false);
  const [machineLastTapTime, setMachineLastTapTime] = useState(0);

  // 자재 스캔 중복 방지
  const [lastScannedBarcode, setLastScannedBarcode] = useState('');
  const [isScanning, setIsScanning] = useState(false);

  // 기계 스캔 중복 방지
  const [lastScannedMachineBarcode, setLastScannedMachineBarcode] = useState('');
  const [isMachineScanning, setIsMachineScanning] = useState(false);

  // ===== 실장 버튼 활성화 조건 =====
  const isMatchValid =
    scannedLot !== null &&
    scannedLot.matched &&
    machineFeederId.trim() !== '' &&
    machineFeederId.trim() === scannedLot.feederCd;

  // ===== 기계 피더 일치 여부 (테두리 색상용) =====
  const isMachineFeederMatched =
    machineFeederId !== '' &&
    scannedLot !== null &&
    machineFeederId.trim() === scannedLot.feederCd;

  const isMachineFeederMismatched =
    machineFeederId !== '' &&
    scannedLot !== null &&
    machineFeederId.trim() !== scannedLot.feederCd;

  // ===== 자재 스캔 핸들러 =====
  const handleBarcodeScan = async (scannedValue: string) => {
    if (isScanning || lastScannedBarcode === scannedValue) return;

    setIsScanning(true);
    setLastScannedBarcode(scannedValue);
    setBarcode(scannedValue);

    // 자재 재스캔 시 기계 스캔 초기화
    resetMachineScan();

    try {
      const result = await onScanLot(scannedValue);
      if (result) {
        setBarcode('');
        setLastScannedBarcode('');

        // 자재 스캔 성공 → 기계 Scan으로 포커스 이동
        setTimeout(() => {
          machineScanRef.current?.focus();
        }, 100);
      } else {
        setBarcode('');
      }
    } catch (err) {
      console.error('자재 LOT 스캔 실패:', err);
      setBarcode('');
    } finally {
      setIsScanning(false);
      setTimeout(() => setLastScannedBarcode(''), 1000);
    }
  };

  // ===== 기계 스캔 핸들러 =====
  const handleMachineBarcodeScan = (scannedValue: string) => {
    // 자재 스캔 완료 후에만 기계 스캔 가능
    if (!scannedLot || !scannedLot.feederCd) {
      warning('자재를 먼저 스캔하세요.');
      setMachineBarcode('');
      return;
    }

    if (isMachineScanning || lastScannedMachineBarcode === scannedValue) return;

    setIsMachineScanning(true);
    setLastScannedMachineBarcode(scannedValue);

    const trimmed = scannedValue.trim();

    // 기계 피더정보 세팅
    setMachineFeederId(trimmed);
    setMachineBarcode('');

    // 자재 스캔이 완료된 상태라면 즉시 비교
    if (scannedLot !== null) {
      if (trimmed !== scannedLot.feederCd) {
        warning(`피더 불일치: 기계(${trimmed}) ≠ 자재(${scannedLot.feederCd})`);
      }
    }

    // 기계 스캔 후 기계 Scan input으로 포커스 유지
    setTimeout(() => {
      machineScanRef.current?.focus();
      setIsMachineScanning(false);
      setLastScannedMachineBarcode('');
    }, 1000);
  };

  // ===== useBarcodeScan 훅 (자재) =====
  const {
    scanInputRef: materialScanRef,
    handleScanKeyDown: handleMaterialKeyDown,
    handleInput: handleMaterialInput,
  } = useBarcodeScan(handleBarcodeScan, {
    clearOnScan: false,
    refocusOnScan: false, // 자재 스캔 후 기계로 이동하므로 false
    debounceMs: 300,
    autoFocus: true,
  });

  // ===== useBarcodeScan 훅 (기계) =====
  const {
    scanInputRef: machineScanRef,
    handleScanKeyDown: handleMachineKeyDown,
    handleInput: handleMachineInput,
  } = useBarcodeScan(handleMachineBarcodeScan, {
    clearOnScan: false,
    refocusOnScan: true, // 기계 스캔 후 기계 input에 포커스 유지
    debounceMs: 300,
    autoFocus: false,
  });

  // ===== 실장 버튼 클릭 =====
  const handleMountClick = () => {
    if (!isMatchValid) return;
    onMount();

    // 실장 후 자재 Scan으로 포커스 이동
    setTimeout(() => {
      materialScanRef.current?.focus();
    }, 100);
  };

  // ===== 완료 버튼 클릭 =====
  const handleCompleteClick = async () => {
    await onComplete();

    // 완료 후 자재 Scan으로 포커스 이동
    setTimeout(() => {
      materialScanRef.current?.focus();
    }, 100);
  };

  const handleMountKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleMountClick();
    }
  };

  // ===== 더블탭 키보드 활성화 =====
  const handleMaterialDoubleTap = () => {
    const now = Date.now();
    if (now - materialLastTapTime < 300) {
      setMaterialKeyboardEnabled(true);
      materialScanRef.current?.focus();
    }
    setMaterialLastTapTime(now);
  };

  const handleMachineDoubleTap = () => {
    const now = Date.now();
    if (now - machineLastTapTime < 300) {
      setMachineKeyboardEnabled(true);
      machineScanRef.current?.focus();
    }
    setMachineLastTapTime(now);
  };

  const toggleMaterialKeyboard = () => {
    setMaterialKeyboardEnabled(!materialKeyboardEnabled);
    if (!materialKeyboardEnabled) {
      setTimeout(() => materialScanRef.current?.focus(), 100);
    }
  };

  const toggleMachineKeyboard = () => {
    setMachineKeyboardEnabled(!machineKeyboardEnabled);
    if (!machineKeyboardEnabled) {
      setTimeout(() => machineScanRef.current?.focus(), 100);
    }
  };

  const getMaterialName = () => {
    if (!scannedLot) return '';
    const name = scannedLot.itemNm || '';
    const spec = scannedLot.itemSpec || '';
    return spec ? `${name}/${spec}` : name;
  };

  const handleFeederKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
      if (scannedLot?.feederCd) {
        const ok = copyToClipboard(scannedLot.feederCd);
      }
    }
  };

  // 키보드 아이콘 SVG
  const KeyboardIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
      />
    </svg>
  );

  return (
    <div className="w-full md:w-45 bg-white p-4 pb-1 border-r border-gray-200 overflow-y-auto">
      {selectedOrder && (
        <div className="space-y-1">

          {/* 공정지시No */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-24 flex-shrink-0">공정지시No</label>
            <input
              type="text"
              value={selectedOrder.productWorkNo || ''}
              readOnly
              className="flex-1 px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black"
            />
          </div>

          {/* 품목 */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-24 flex-shrink-0">품목</label>
            <input
              type="text"
              value={selectedOrder.itemNm || ''}
              readOnly
              className="flex-1 px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black"
            />
          </div>

          {/* 자재 Scan */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-24 flex-shrink-0">자재 Scan</label>
            <div className="flex-1 flex gap-1">
              <input
                ref={materialScanRef}
                type="text"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                onInput={handleMaterialInput}
                onKeyDown={handleMaterialKeyDown}
                onClick={handleMaterialDoubleTap}
                inputMode={materialKeyboardEnabled ? 'text' : 'none'}
                autoComplete="off"
                placeholder="LOT 번호 스캔"
                className="flex-1 max-w-[197px] px-2 text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={toggleMaterialKeyboard}
                className={`w-10 flex-shrink-0 px-2 border border-gray-300 rounded transition-colors ${
                  materialKeyboardEnabled
                    ? 'bg-blue-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <KeyboardIcon />
              </button>
            </div>
          </div>

          {/* 자재품명 */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-24 flex-shrink-0">자재품명</label>
            <input
              type="text"
              value={getMaterialName()}
              readOnly
              className="flex-1 px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black"
            />
          </div>

          {/* 피더정보 / Lot량 (한 행 배치) */}
          <div className="flex items-center gap-1">
            <div className="flex items-center gap-1">
              <label className="text-lg font-medium text-black w-24 flex-shrink-0">피더정보</label>
              <input
                type="text"
                value={scannedLot?.feederCd || ''}
                readOnly
                onKeyDown={handleFeederKeyDown}
                className="w-24 px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black focus:outline-none focus:ring-2 focus:ring-blue-300 cursor-pointer"
              />
            </div>
            <div className="flex items-center gap-1">
              <label className="text-lg mx-1 font-medium text-black w-12 flex-shrink-0">Lot량</label>
              <input
                type="text"
                value={scannedLot ? `${scannedLot.lotQty.toLocaleString()}` : ''}
                readOnly
                className="w-16 px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black"
              />
            </div>
          </div>

          {/* 기계 Scan */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-24 flex-shrink-0">기계 Scan</label>
            <div className="flex-1 flex gap-1">
              <input
                ref={machineScanRef}
                type="text"
                value={machineBarcode}
                disabled={!scannedLot?.feederCd}
                onChange={(e) => setMachineBarcode(e.target.value)}
                onInput={handleMachineInput}
                onKeyDown={handleMachineKeyDown}
                onClick={handleMachineDoubleTap}
                inputMode={machineKeyboardEnabled ? 'text' : 'none'}
                autoComplete="off"
                placeholder={!scannedLot?.feederCd ? '자재를 먼저 스캔하세요' : '기계 바코드 스캔'}
                className={`flex-1 max-w-[197px] px-2 text-lg border rounded text-black
                  focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                  !scannedLot?.feederCd
                    ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                    : isMachineFeederMatched
                    ? 'bg-green-50 border-green-500'
                    : isMachineFeederMismatched
                    ? 'bg-red-50 border-red-500'
                    : 'bg-white border-gray-300'
                }`}
              />
              <button
                type="button"
                onClick={toggleMachineKeyboard}
                className={`w-10 flex-shrink-0 px-2 border border-gray-300 rounded transition-colors ${
                  machineKeyboardEnabled
                    ? 'bg-blue-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <KeyboardIcon />
              </button>
            </div>
          </div>

          {/* 기계 피더정보 (기계 스캔 결과) */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-24 flex-shrink-0">기계 피더</label>
            <input
              type="text"
              value={machineFeederId}
              readOnly
              className={`flex-1 px-2 text-lg border rounded bg-gray-100 text-black transition-colors ${
                isMachineFeederMatched
                  ? 'border-green-500 bg-green-50'
                  : isMachineFeederMismatched
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-300'
              }`}
            />
          </div>

          {/* 하단 버튼 */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleMountClick}
              onKeyPress={handleMountKeyPress}
              disabled={!isMatchValid}
              className={`flex-1 px-4 py-1 rounded text-lg font-medium transition-colors ${
                isMatchValid
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              실장
            </button>
            <button
              onClick={handleCompleteClick}
              disabled={checkList.length === 0}
              className={`flex-1 px-4 py-1 rounded text-lg font-medium transition-colors ${
                checkList.length > 0
                  ? 'bg-green-500 text-white hover:bg-green-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              완료 ({checkList.length})
            </button>
          </div>

        </div>
      )}
    </div>
  );
};

export default PDA1120_R01Info;
